import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { aiService } from "@/services/aiService";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useStore } from "@/contexts/StoreContext";
import { CONVERSATION_LIMITS } from "@/lib/constants";
import { categorizeError } from "@/lib/errorHandling";
import { useQueryClient } from "@tanstack/react-query";
import { invalidateChatQueries } from "@/lib/queryInvalidation";

export interface ImageAttachment {
  base64: string;
  filename: string;
  mimeType: string;
}

export interface ChartDataPoint {
  label: string;
  value: number;
}

export interface AnalyticsContext {
  ordersAnalyzed: number;
  dateRange: { from: string; to: string };
  codeExecuted?: boolean;
}

export interface ChatMessage {
  id?: string;
  role: "user" | "assistant";
  content: string;
  timestamp: string;
  isTyping?: boolean;
  images?: ImageAttachment[];
  actions?: Array<{ label: string; type: string }>;
  actionResult?: {
    toolName: string | null;
    success: boolean;
    data?: any;
    error?: string;
  };
  failed?: boolean;
  retryCount?: number;
  errorDetails?: {
    title: string;
    description: string;
    canRetry: boolean;
  };
  // Analytics visualization fields
  chartType?: "bar" | "line" | "pie" | "table" | null;
  chartData?: ChartDataPoint[];
  breakdown?: Array<{ label: string; value: any }>;
  analyticsContext?: AnalyticsContext;
}

export interface ChatAction {
  label: string;
  type: string;
}

export interface Conversation {
  id: string;
  title: string;
  messages: ChatMessage[];
  createdAt: string;
  updatedAt?: string;
  storeId?: string | null;
}

export interface PendingProductChange {
  productId: string;
  productName: string;
  changes: Array<{
    field: string;
    label: string;
    before: string;
    after: string;
  }>;
}

export interface PendingProductCreation {
  storeId: string;
  name: string;
  description?: string;
  short_description?: string;
  regular_price?: string;
  sku?: string;
}

export interface ChatContextType {
  messages: ChatMessage[];
  conversations: Conversation[];
  archivedConversations: Conversation[];
  currentConversationId: string | null;
  isOpen: boolean;
  isMinimized: boolean;
  unreadCount: number;
  context: string;
  hasStores: boolean;
  pendingChanges: PendingProductChange | null;
  pendingProductCreation: PendingProductCreation | null;
  isLoading: boolean;
  isLoadingMessages: boolean;
  isLoadingArchived: boolean;
  regeneratingMessageId: string | null;
  sendMessage: (content: string, images?: ImageAttachment[], forceNewConversation?: boolean) => Promise<void>;
  retryMessage: (messageId: string) => Promise<void>;
  regenerateResponse: (messageId: string) => Promise<void>;
  openChat: () => void;
  closeChat: () => void;
  minimizeChat: () => void;
  toggleChat: () => void;
  setContext: (context: string) => void;
  clearContext: () => void;
  clearMessages: () => void;
  createNewConversation: () => void;
  switchConversation: (id: string) => Promise<void>;
  deleteConversation: (id: string) => Promise<void>;
  renameConversation: (id: string, newTitle: string) => Promise<void>;
  restoreConversation: (id: string) => Promise<void>;
  permanentlyDeleteConversation: (id: string) => Promise<void>;
  loadArchivedConversations: () => Promise<void>;
  clearPendingChanges: () => void;
  approvePendingChanges: (approvedChanges: Array<{ field: string; value: string }>) => Promise<void>;
  clearPendingProductCreation: () => void;
  approvePendingProductCreation: () => Promise<void>;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

const DEFAULT_CONVERSATION_TITLE = "New chat";

export function ChatProvider({ children, hasStores = false }: { children: ReactNode; hasStores?: boolean }) {
  const { user } = useAuth();
  const { currentStore } = useStore();
  const queryClient = useQueryClient();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [archivedConversations, setArchivedConversations] = useState<Conversation[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [chatContext, setChatContext] = useState<string>('');
  const [pendingChanges, setPendingChanges] = useState<PendingProductChange | null>(null);
  const [pendingProductCreation, setPendingProductCreation] = useState<PendingProductCreation | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isLoadingArchived, setIsLoadingArchived] = useState(false);
  const [regeneratingMessageId, setRegeneratingMessageId] = useState<string | null>(null);

  const createMessageId = () => `msg-${Date.now()}-${Math.random().toString(16).slice(2)}`;

  // Load conversations from Supabase on mount
  useEffect(() => {
    if (!user) {
      setConversations([]);
      setCurrentConversationId(null);
      setIsLoading(false);
      return;
    }

    const loadConversations = async () => {
      setIsLoading(true);
      try {
        // Load ONLY the most recent active conversations (limit enforced by trigger)
        const { data: convData, error: convError } = await supabase
          .from('chat_conversations')
          .select('*')
          .eq('user_id', user.id)
          .is('archived_at', null)
          .order('updated_at', { ascending: false })
          .limit(CONVERSATION_LIMITS.MAX_ACTIVE);

        if (convError) throw convError;

        if (!convData || convData.length === 0) {
          // No conversations, start fresh
          setConversations([]);
          setCurrentConversationId(null);
          setIsLoading(false);
          return;
        }

        // OPTIMIZATION: Only load messages for the FIRST (most recent) conversation
        // Other conversations will lazy-load their messages when switched to
        const activeConvId = convData[0].id;
        const { data: msgData, error: msgError } = await supabase
          .from('chat_messages')
          .select('*')
          .eq('conversation_id', activeConvId)
          .order('created_at', { ascending: true });

        if (msgError) throw msgError;

        // Build conversations - only the active one has messages loaded
        const loadedConversations: Conversation[] = convData.map(conv => ({
          id: conv.id,
          title: conv.title,
          createdAt: conv.created_at || new Date().toISOString(),
          updatedAt: conv.updated_at || undefined,
          storeId: conv.store_id,
          // Only load messages for the active conversation
          messages: conv.id === activeConvId
            ? (msgData || []).map(msg => {
                const metadata = msg.metadata as any;
                return {
                  id: msg.id,
                  role: msg.role as 'user' | 'assistant',
                  content: msg.content,
                  timestamp: msg.created_at || new Date().toISOString(),
                  actionResult: metadata?.actionResult,
                  images: metadata?.images,
                };
              })
            : [], // Empty array - will be lazy loaded when switched to
        }));

        setConversations(loadedConversations);
        setCurrentConversationId(activeConvId);
      } catch (error) {
        console.error('Error loading conversations:', error);
        toast({
          title: "Failed to load conversations",
          description: "Please refresh the page to try again.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadConversations();
  }, [user]);

  const currentConversation = conversations.find(c => c.id === currentConversationId);

  // Save message to Supabase
  const saveMessage = async (conversationId: string, message: ChatMessage) => {
    if (!user) return;

    try {
      // Prepare metadata: combine actionResult and images
      const metadata: any = {};
      if (message.actionResult) {
        metadata.actionResult = message.actionResult;
      }
      if (message.images && message.images.length > 0) {
        metadata.images = message.images;
      }

      await supabase.from('chat_messages').insert({
        conversation_id: conversationId,
        role: message.role,
        content: message.content,
        metadata: Object.keys(metadata).length > 0 ? metadata : null,
      });

      // Update conversation's updated_at
      await supabase
        .from('chat_conversations')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', conversationId);
    } catch (error) {
      console.error('Error saving message:', error);
    }
  };

  // Create new conversation in Supabase
  const createConversationInDb = async (title: string): Promise<string | null> => {
    if (!user) return null;

    try {
      const { data, error } = await supabase
        .from('chat_conversations')
        .insert({
          user_id: user.id,
          title,
        })
        .select('id')
        .single();

      if (error) throw error;
      return data.id;
    } catch (error) {
      console.error('Error creating conversation:', error);
      return null;
    }
  };

  const updateConversationMessages = useCallback((conversationId: string, updater: (messages: ChatMessage[]) => ChatMessage[]) => {
    setConversations(prev => 
      prev.map(conv => 
        conv.id === conversationId 
          ? { ...conv, messages: updater(conv.messages), updatedAt: new Date().toISOString() }
          : conv
      )
    );
  }, []);

  const buildFallbackTitle = (content: string): string => {
    const cleaned = content.replace(/\s+/g, ' ').trim();
    if (!cleaned) {
      return DEFAULT_CONVERSATION_TITLE;
    }
    const words = cleaned.split(' ');
    const short = words.slice(0, 6).join(' ');
    return words.length > 6 ? `${short}...` : short;
  };

  const isDefaultTitle = (title: string | null | undefined) =>
    (title || "").trim().toLowerCase() === DEFAULT_CONVERSATION_TITLE.toLowerCase();

  const sendMessage = useCallback(async (content: string, images?: ImageAttachment[], forceNewConversation?: boolean) => {
    if (!user) {
      toast({
        title: "Not logged in",
        description: "Please log in to use the chat.",
        variant: "destructive",
      });
      return;
    }

    // Check rate limit using raw SQL call to avoid TypeScript issues with custom functions
    try {
      const { data: rateLimitData, error: rateLimitError } = await supabase
        .rpc('check_chat_rate_limit' as any, { p_user_id: user.id });

      if (rateLimitError) {
        console.error('Rate limit check error:', rateLimitError);
        // Continue anyway - don't block on rate limit check errors
      } else if (rateLimitData && Array.isArray(rateLimitData) && rateLimitData.length > 0) {
        const limit = rateLimitData[0] as { allowed: boolean; limit_type: string; message: string };

        if (!limit.allowed) {
          // Show appropriate error based on limit type
          const upgradeAction = limit.limit_type === 'monthly' ? 'Upgrade your plan for more messages.' : '';

          toast({
            title: limit.limit_type === 'per_minute' ? 'Slow down!' : 'Rate limit reached',
            description: `${limit.message} ${upgradeAction}`,
            variant: "destructive",
          });
          return;
        }
      }
    } catch (error) {
      console.error('Error checking rate limit:', error);
      // Continue anyway - don't block on rate limit check errors
    }

    // If forceNewConversation is true, ignore current conversation and create a new one
    let activeConversationId = forceNewConversation ? null : currentConversationId;

    // Check if current conversation is a draft (not yet persisted to DB)
    const isDraft = activeConversationId?.startsWith('draft-');

    // If no current conversation or it's a draft, create a real one in the database
    if (!activeConversationId || isDraft) {
      const newId = await createConversationInDb(DEFAULT_CONVERSATION_TITLE);
      if (!newId) {
        toast({
          title: "Error",
          description: "Failed to create conversation.",
          variant: "destructive",
        });
        return;
      }

      if (isDraft && activeConversationId) {
        // Replace the draft conversation with the real one
        setConversations(prev => 
          prev.map(conv => 
            conv.id === activeConversationId 
              ? { ...conv, id: newId, title: DEFAULT_CONVERSATION_TITLE } 
              : conv
          )
        );
      } else {
        // Create new conversation entry
        const newConversation: Conversation = {
          id: newId,
          title: DEFAULT_CONVERSATION_TITLE,
          messages: [],
          createdAt: new Date().toISOString(),
        };
        setConversations(prev => [newConversation, ...prev]);
      }
      
      setCurrentConversationId(newId);
      activeConversationId = newId;
    }

    // Add user message
    const userMessage: ChatMessage = {
      id: createMessageId(),
      role: 'user',
      content,
      timestamp: new Date().toISOString(),
      images,
    };

    updateConversationMessages(activeConversationId, (messages) => [
      ...messages,
      userMessage
    ]);

    // Save to DB
    await saveMessage(activeConversationId, userMessage);

    // Get current messages for context - use the state BEFORE this message was added
    const currentMessages = conversations.find(c => c.id === activeConversationId)?.messages || [];
    
    // Track if this is a new conversation that needs title generation
    // We check BEFORE we add the user message, so currentMessages being empty means this is the first message
    const isNewConversation = currentMessages.length === 0;

    // Add typing indicator
    const typingMessage: ChatMessage = {
      id: createMessageId(),
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
      isTyping: true,
    };

    updateConversationMessages(activeConversationId, (messages) => [
      ...messages,
      typingMessage
    ]);

    // Call AI service
    try {
      // Request title generation for first message exchange (use pre-calculated flag)
      const shouldGenerateTitle = isNewConversation;

      // Check if we're on Analytics/Insights page - route to interpreter
      const isAnalyticsContext = chatContext === 'Analytics' || chatContext === 'Insights';

      let data;
      if (isAnalyticsContext && currentStore?.id) {
        // Use the analytics interpreter for analytics-specific questions
        data = await aiService.analyticsChat(content, currentStore.id);
      } else {
        // Use regular chat for all other contexts
        data = await aiService.chat(
          [...currentMessages, userMessage],
          { currentPage: chatContext, hasStores, storeId: currentStore?.id },
          images,
          activeConversationId,
          shouldGenerateTitle
        );
      }

      if (!data.success) {
        throw new Error(data.error || 'Failed to get response');
      }

      // Update local conversation title if AI generated one
      if (data.generatedTitle && activeConversationId) {
        setConversations(prev => {
          const existingConv = prev.find(c => c.id === activeConversationId);
          if (existingConv) {
            // Conversation exists, just update title
            return prev.map(conv =>
              conv.id === activeConversationId
                ? { ...conv, title: data.generatedTitle! }
                : conv
            );
          } else {
            // Conversation was just created but not in state yet (race condition)
            // Add it with the AI-generated title
            return [{
              id: activeConversationId,
              title: data.generatedTitle!,
              messages: [],
              createdAt: new Date().toISOString(),
            }, ...prev];
          }
        });
      } else if (shouldGenerateTitle && activeConversationId) {
        const fallbackTitle = buildFallbackTitle(content);
        setConversations(prev =>
          prev.map(conv =>
            conv.id === activeConversationId && isDefaultTitle(conv.title)
              ? { ...conv, title: fallbackTitle }
              : conv
          )
        );
      }

      // Check if this is a product modification with live preview
      if (data.actionResult?.toolName === 'update_product' && data.actionResult.success) {
        const productData = data.actionResult.data;

        // If there's a preview URL, open it (same as product creation flow)
        if (productData?.previewUrl) {
          const newTab = window.open(productData.previewUrl, "_blank");
          if (!newTab) {
            toast({
              title: "Preview created!",
              description: "Click 'Open' in the pending previews bar below to view your changes.",
            });
          } else {
            toast({
              title: "Preview created!",
              description: "Review the changes on your store, then approve or reject below.",
            });
          }
        } else if (productData?.existingPreview) {
          toast({
            title: "Existing preview pending",
            description: productData.message || "Please approve or reject the existing preview first.",
            variant: "destructive",
          });
        }

        const aiMessage: ChatMessage = {
          id: createMessageId(),
          role: 'assistant',
          content: data.content || 'I\'ve created a live preview of your changes. Review them on your store, then approve or reject using the bar below.',
          timestamp: new Date().toISOString(),
          actions: data.actions,
          actionResult: data.actionResult,
        };

        updateConversationMessages(activeConversationId, (messages) => [
          ...messages.filter(m => !m.isTyping),
          aiMessage
        ]);
        await saveMessage(activeConversationId, aiMessage);
      } else {
        const aiMessage: ChatMessage = {
          id: createMessageId(),
          role: 'assistant',
          content: data.content || 'I encountered an error processing your request.',
          timestamp: new Date().toISOString(),
          actions: data.actions,
          actionResult: data.actionResult,
          // Include analytics visualization data if present
          chartType: data.chartType,
          chartData: data.chartData,
          breakdown: data.breakdown,
          analyticsContext: data.analyticsContext,
        };

        updateConversationMessages(activeConversationId, (messages) => [
          ...messages.filter(m => !m.isTyping),
          aiMessage
        ]);
        await saveMessage(activeConversationId, aiMessage);

        // Handle product creation with preview
        if (data.actionResult?.toolName === 'create_product' && data.actionResult.success) {
          const productData = data.actionResult.data;
          if (productData?.pending) {
            setPendingProductCreation({
              storeId: productData.storeId,
              name: productData.name,
              description: productData.description,
              short_description: productData.short_description,
              regular_price: productData.regular_price,
              sku: productData.sku,
            });
          } else {
            const productName = productData?.product?.name || 'Product';
            toast({
              title: "Product Created!",
              description: `"${productName}" has been added to your store.`,
            });
          }
        }

        // Handle image upload to product
        if (data.actionResult?.toolName === 'add_images_to_product' && data.actionResult.success) {
          const uploadData = data.actionResult.data;
          const { productName, successCount, failedCount } = uploadData;

          if (failedCount === 0) {
            toast({
              title: "Images Uploaded!",
              description: `Successfully added ${successCount} image(s) to "${productName}".`,
            });
          } else {
            toast({
              title: "Partial Upload",
              description: `Added ${successCount} image(s) to "${productName}". ${failedCount} failed.`,
              variant: "destructive",
            });
          }
        }

        // Handle query invalidation hints from AI tools (category/menu operations)
        if (data.actionResult?.success && data.actionResult?.data?.invalidate_queries) {
          const queriesToInvalidate = data.actionResult.data.invalidate_queries;
          if (Array.isArray(queriesToInvalidate) && queriesToInvalidate.length > 0) {
            console.log('Invalidating queries from AI action:', queriesToInvalidate);
            invalidateChatQueries(queryClient, queriesToInvalidate);
          }
        }
      }

      // Track successful chat message usage
      try {
        await supabase.rpc('track_chat_message' as any, {
          p_user_id: user.id,
          p_conversation_id: activeConversationId,
          p_message_length: content.length,
        });
      } catch (trackError) {
        console.error('Error tracking chat usage:', trackError);
        // Don't fail the chat if tracking fails
      }

      if (!isOpen && !isMinimized) {
        setUnreadCount((prev) => prev + 1);
      }
    } catch (error) {
      console.error('Chat error:', error);

      // Categorize the error
      const categorized = categorizeError(error);

      // Remove typing indicator and mark the user message as failed
      updateConversationMessages(activeConversationId, (messages) =>
        messages
          .filter(m => !m.isTyping)
          .map(m =>
            m.id === userMessage.id
              ? {
                  ...m,
                  failed: true,
                  retryCount: (m.retryCount || 0) + 1,
                  errorDetails: {
                    title: categorized.title,
                    description: categorized.description,
                    canRetry: categorized.canRetry,
                  },
                }
              : m
          )
      );

      // Show error toast
      toast({
        title: categorized.title,
        description: categorized.description,
        variant: "destructive",
      });
    }
  }, [user, conversations, currentConversationId, chatContext, isOpen, isMinimized, updateConversationMessages, hasStores]);

  const retryMessage = useCallback(async (messageId: string) => {
    if (!currentConversationId) return;

    const conversation = conversations.find(c => c.id === currentConversationId);
    if (!conversation) return;

    const failedMessage = conversation.messages.find(m => m.id === messageId);
    if (!failedMessage || failedMessage.role !== 'user') return;

    // Check retry limit (max 3 retries)
    if ((failedMessage.retryCount || 0) >= 3) {
      toast({
        title: "Retry limit reached",
        description: "This message has failed too many times. Please try rephrasing your question.",
        variant: "destructive",
      });
      return;
    }

    // Reset failed state
    updateConversationMessages(currentConversationId, (messages) =>
      messages.map(m =>
        m.id === messageId
          ? { ...m, failed: false, errorDetails: undefined }
          : m
      )
    );

    // Retry sending the message
    await sendMessage(failedMessage.content, failedMessage.images);
  }, [conversations, currentConversationId, sendMessage, updateConversationMessages]);

  const regenerateResponse = useCallback(async (assistantMessageId: string) => {
    if (!currentConversationId || !user) return;

    // Guard: prevent duplicate regenerations
    if (regeneratingMessageId) {
      console.log('Regeneration already in progress, ignoring click');
      return;
    }

    const conversation = conversations.find(c => c.id === currentConversationId);
    if (!conversation) return;

    // Find the assistant message and validate
    const msgIndex = conversation.messages.findIndex(m => m.id === assistantMessageId);
    if (msgIndex < 0) return;

    const assistantMessage = conversation.messages[msgIndex];
    if (assistantMessage.role !== 'assistant') {
      console.warn('regenerateResponse called on non-assistant message');
      return;
    }

    // Find the preceding user message
    const userMessage = msgIndex > 0 ? conversation.messages[msgIndex - 1] : null;
    if (!userMessage || userMessage.role !== 'user') {
      console.warn('No preceding user message found for regeneration');
      return;
    }

    // Set regeneration guard
    setRegeneratingMessageId(assistantMessageId);

    // Replace assistant message IN-PLACE with typing indicator (same id, same position)
    updateConversationMessages(currentConversationId, messages =>
      messages.map(m =>
        m.id === assistantMessageId
          ? {
              ...m,
              content: '',
              isTyping: true,
              actionResult: undefined,
              actions: undefined,
              chartType: undefined,
              chartData: undefined,
              breakdown: undefined,
              analyticsContext: undefined,
              failed: false,
              errorDetails: undefined,
            }
          : m
      )
    );

    // Build conversation history UP TO (not including) the assistant message
    // This already includes the user message at msgIndex-1
    const messagesForContext = conversation.messages.slice(0, msgIndex);

    try {
      // Check if we're on Analytics/Insights page - route to interpreter
      const isAnalyticsContext = chatContext === 'Analytics' || chatContext === 'Insights';

      let data;
      if (isAnalyticsContext && currentStore?.id) {
        data = await aiService.analyticsChat(userMessage.content, currentStore.id);
      } else {
        // Pass messagesForContext directly - it already includes the user message
        // DO NOT append userMessage again (that was causing duplicates)
        data = await aiService.chat(
          messagesForContext,
          { currentPage: chatContext, hasStores, storeId: currentStore?.id },
          userMessage.images,
          currentConversationId,
          false // Don't regenerate title
        );
      }

      if (!data.success) {
        throw new Error(data.error || 'Failed to get response');
      }

      // Replace typing indicator with actual response (same id, same position)
      updateConversationMessages(currentConversationId, (messages) =>
        messages.map(m =>
          m.id === assistantMessageId
            ? {
                id: assistantMessageId,
                role: 'assistant' as const,
                content: data.content || '',
                timestamp: new Date().toISOString(),
                isTyping: false,
                actions: data.actions,
                actionResult: data.actionResult,
                chartType: data.chartType,
                chartData: data.chartData,
                breakdown: data.breakdown,
                analyticsContext: data.analyticsContext,
              }
            : m
        )
      );

      // Update in database - try UPDATE first, fall back to delete+insert
      const isDbId = assistantMessageId && !assistantMessageId.startsWith('msg-');
      if (isDbId) {
        // Update existing row
        const { error: updateError } = await supabase
          .from('chat_messages')
          .update({
            content: data.content || '',
            metadata: {
              actions: data.actions,
              actionResult: data.actionResult,
              chartType: data.chartType,
              chartData: data.chartData,
              breakdown: data.breakdown,
              analyticsContext: data.analyticsContext,
            },
          })
          .eq('id', assistantMessageId);

        if (updateError) {
          console.error('Failed to update message in database:', updateError);
        }
      } else {
        // Local-only message - just save new (old approach)
        await saveMessage(currentConversationId, {
          id: assistantMessageId,
          role: 'assistant',
          content: data.content || '',
          timestamp: new Date().toISOString(),
          actions: data.actions,
          actionResult: data.actionResult,
          chartType: data.chartType,
          chartData: data.chartData,
          breakdown: data.breakdown,
          analyticsContext: data.analyticsContext,
        });
      }

      // Handle pending changes if product modification
      if (data.actionResult?.toolName === 'update_product' && data.actionResult.success) {
        const productData = data.actionResult.data;
        if (productData?.pendingChanges) {
          setPendingChanges(productData.pendingChanges);
        }
      }

    } catch (error) {
      console.error('Regenerate response error:', error);

      const errorInfo = categorizeError(error);

      // Replace typing indicator with error state (same id, same position)
      updateConversationMessages(currentConversationId, (messages) =>
        messages.map(m =>
          m.id === assistantMessageId
            ? {
                id: assistantMessageId,
                role: 'assistant' as const,
                content: errorInfo.description,
                timestamp: new Date().toISOString(),
                isTyping: false,
                failed: true,
                errorDetails: {
                  title: errorInfo.title,
                  description: errorInfo.description,
                  canRetry: true,
                },
              }
            : m
        )
      );

      toast({
        title: errorInfo.title,
        description: errorInfo.description,
        variant: "destructive",
      });
    } finally {
      // Clear regeneration guard
      setRegeneratingMessageId(null);
    }
  }, [conversations, currentConversationId, user, chatContext, hasStores, currentStore, updateConversationMessages, regeneratingMessageId]);

  const openChat = useCallback(() => {
    setIsOpen(true);
    setIsMinimized(false);
    setUnreadCount(0);
  }, []);

  const closeChat = useCallback(() => {
    setIsOpen(false);
  }, []);

  const minimizeChat = useCallback(() => {
    setIsMinimized(true);
  }, []);

  const toggleChat = useCallback(() => {
    if (isOpen && !isMinimized) {
      setIsMinimized(true);
    } else {
      openChat();
    }
  }, [isOpen, isMinimized, openChat]);

  const createNewConversation = useCallback(() => {
    if (!user) return;

    const currentConversation = conversations.find(c => c.id === currentConversationId);
    if (
      currentConversationId?.startsWith('draft-') &&
      currentConversation &&
      currentConversation.messages.length === 0
    ) {
      return;
    }

    // Create a temporary draft conversation that appears immediately in the sidebar
    // It will be persisted to DB when the first message is sent
    const draftId = `draft-${Date.now()}`;
    const draftConversation: Conversation = {
      id: draftId,
      title: DEFAULT_CONVERSATION_TITLE,
      messages: [],
      createdAt: new Date().toISOString(),
    };

    // Add to local state and switch to it
    setConversations(prev => [draftConversation, ...prev]);
    setCurrentConversationId(draftId);
  }, [user, conversations, currentConversationId]);

  const switchConversation = useCallback(async (id: string) => {
    const conv = conversations.find(c => c.id === id);

    // Switch to conversation immediately (optimistic UI)
    setCurrentConversationId(id);
    setUnreadCount(0);

    // Skip database fetch for draft conversations (not yet persisted)
    if (id.startsWith('draft-')) {
      return;
    }

    // If conversation exists but messages not loaded yet (empty array), fetch them
    if (conv && conv.messages.length === 0) {
      setIsLoadingMessages(true);
      try {
        const { data: msgData, error } = await supabase
          .from('chat_messages')
          .select('*')
          .eq('conversation_id', id)
          .order('created_at', { ascending: true });

        if (error) throw error;

        if (msgData && msgData.length > 0) {
          // Update the conversation with loaded messages
          setConversations(prev =>
            prev.map(c =>
              c.id === id
                ? {
                    ...c,
                    messages: msgData.map(msg => {
                      const metadata = msg.metadata as any;
                      return {
                        id: msg.id,
                        role: msg.role as 'user' | 'assistant',
                        content: msg.content,
                        timestamp: msg.created_at || new Date().toISOString(),
                        actionResult: metadata?.actionResult,
                        images: metadata?.images,
                      };
                    }),
                  }
                : c
            )
          );
        }
      } catch (error) {
        console.error('Error loading messages:', error);
        toast({
          title: "Failed to load messages",
          description: "Please try switching conversations again.",
          variant: "destructive",
        });
      } finally {
        setIsLoadingMessages(false);
      }
    }
  }, [conversations]);

  // Soft delete - set archived_at
  const deleteConversation = useCallback(async (id: string) => {
    if (!user) return;

    try {
      // Get conversation title for toast notification
      const conversationToDelete = conversations.find(c => c.id === id);

      // Soft delete by setting archived_at
      const { error } = await supabase
        .from('chat_conversations')
        .update({ archived_at: new Date().toISOString() })
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) throw error;

      // Remove from local state
      setConversations(prev => {
        const filtered = prev.filter(c => c.id !== id);
        return filtered;
      });

      if (currentConversationId === id) {
        const remaining = conversations.filter(c => c.id !== id);
        setCurrentConversationId(remaining[0]?.id || null);
      }

      // Show success notification
      toast({
        title: "Conversation deleted",
        description: conversationToDelete?.title ? `"${conversationToDelete.title}" has been archived.` : "The conversation has been archived.",
      });
    } catch (error) {
      console.error('Error archiving conversation:', error);
      toast({
        title: "Failed to delete conversation",
        description: "An error occurred while deleting the conversation. Please try again.",
        variant: "destructive",
      });
    }
  }, [user, conversations, currentConversationId]);

  // Rename conversation
  const renameConversation = useCallback(async (id: string, newTitle: string) => {
    if (!user) return;

    try {
      // Update in database
      const { error } = await supabase
        .from('chat_conversations')
        .update({ title: newTitle })
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) throw error;

      // Update local state
      setConversations(prev =>
        prev.map(conv =>
          conv.id === id ? { ...conv, title: newTitle } : conv
        )
      );
    } catch (error) {
      console.error('Error renaming conversation:', error);
      toast({
        title: "Error",
        description: "Failed to rename conversation.",
        variant: "destructive",
      });
    }
  }, [user]);

  // Load archived conversations on demand
  const loadArchivedConversations = useCallback(async () => {
    if (!user) return;

    setIsLoadingArchived(true);
    try {
      const { data, error } = await supabase
        .from('chat_conversations')
        .select('*')
        .eq('user_id', user.id)
        .not('archived_at', 'is', null)
        .order('archived_at', { ascending: false })
        .limit(50);

      if (error) throw error;

      const archived: Conversation[] = (data || []).map(conv => ({
        id: conv.id,
        title: conv.title,
        createdAt: conv.created_at || new Date().toISOString(),
        updatedAt: conv.updated_at || undefined,
        storeId: conv.store_id,
        messages: [], // Lazy load when restored
      }));

      setArchivedConversations(archived);
    } catch (error) {
      console.error('Error loading archived conversations:', error);
      toast({
        title: "Failed to load archived chats",
        description: "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingArchived(false);
    }
  }, [user]);

  // Restore an archived conversation
  const restoreConversation = useCallback(async (id: string) => {
    if (!user) return;

    try {
      // Unarchive the conversation
      const { error } = await supabase
        .from('chat_conversations')
        .update({ archived_at: null, updated_at: new Date().toISOString() })
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) throw error;

      // Find the conversation in archived list
      const restoredConv = archivedConversations.find(c => c.id === id);
      if (restoredConv) {
        // Move from archived to active
        setArchivedConversations(prev => prev.filter(c => c.id !== id));
        setConversations(prev => [restoredConv, ...prev]);
        setCurrentConversationId(id);
      }

      toast({
        title: "Conversation restored",
        description: "The conversation is now in your active chats.",
      });
    } catch (error) {
      console.error('Error restoring conversation:', error);
      toast({
        title: "Failed to restore conversation",
        description: "Please try again.",
        variant: "destructive",
      });
    }
  }, [user, archivedConversations]);

  // Permanently delete a conversation (hard delete)
  const permanentlyDeleteConversation = useCallback(async (id: string) => {
    if (!user) return;

    try {
      // Get conversation title for toast
      const conversationToDelete = archivedConversations.find(c => c.id === id);

      // Hard delete from database (messages will cascade due to FK constraint)
      const { error } = await supabase
        .from('chat_conversations')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) throw error;

      // Remove from archived state
      setArchivedConversations(prev => prev.filter(c => c.id !== id));

      toast({
        title: "Conversation permanently deleted",
        description: conversationToDelete?.title 
          ? `"${conversationToDelete.title}" has been permanently deleted.` 
          : "The conversation has been permanently deleted.",
      });
    } catch (error) {
      console.error('Error permanently deleting conversation:', error);
      toast({
        title: "Failed to delete conversation",
        description: "Please try again.",
        variant: "destructive",
      });
    }
  }, [user, archivedConversations]);

  const clearPendingChanges = useCallback(() => {
    setPendingChanges(null);
  }, []);

  const approvePendingChanges = useCallback(async (approvedChanges: Array<{ field: string; value: string }>) => {
    if (!pendingChanges || !currentConversationId) return;

    try {
      const { woocommerceService } = await import('@/services/woocommerce');
      const updates: Record<string, string> = {};
      approvedChanges.forEach(change => {
        updates[change.field] = change.value;
      });

      await woocommerceService.updateProduct(pendingChanges.productId, updates);
      
      setPendingChanges(null);

      const successMessage: ChatMessage = {
        id: createMessageId(),
        role: 'assistant',
        content: `Successfully applied ${approvedChanges.length} change(s) to "${pendingChanges.productName}" and synced to WooCommerce.`,
        timestamp: new Date().toISOString(),
      };

      updateConversationMessages(currentConversationId, (messages) => [
        ...messages,
        successMessage
      ]);
      await saveMessage(currentConversationId, successMessage);
    } catch (error) {
      console.error('Failed to apply changes:', error);
      
      const errorMessage: ChatMessage = {
        role: 'assistant',
        content: 'Failed to sync changes to WooCommerce. Please try again.',
        timestamp: new Date().toISOString(),
      };

      updateConversationMessages(currentConversationId, (messages) => [
        ...messages,
        errorMessage
      ]);
    }
  }, [pendingChanges, currentConversationId, updateConversationMessages]);

  const clearPendingProductCreation = useCallback(() => {
    setPendingProductCreation(null);
  }, []);

  const approvePendingProductCreation = useCallback(async () => {
    if (!pendingProductCreation || !currentConversationId) return;

    try {
      const { woocommerceService } = await import('@/services/woocommerce');
      const result = await woocommerceService.createProduct(pendingProductCreation.storeId, {
        name: pendingProductCreation.name,
        description: pendingProductCreation.description,
        short_description: pendingProductCreation.short_description,
        regular_price: pendingProductCreation.regular_price,
        sku: pendingProductCreation.sku,
      });

      setPendingProductCreation(null);

      toast({
        title: "Product Created!",
        description: `"${result.product?.name || pendingProductCreation.name}" has been added to your store.`,
      });

      const successMessage: ChatMessage = {
        id: createMessageId(),
        role: 'assistant',
        content: `Successfully created "${pendingProductCreation.name}" in your WooCommerce store.`,
        timestamp: new Date().toISOString(),
        actionResult: {
          toolName: 'create_product',
          success: true,
          data: result,
        },
      };

      updateConversationMessages(currentConversationId, (messages) => [
        ...messages,
        successMessage
      ]);
      await saveMessage(currentConversationId, successMessage);
    } catch (error) {
      console.error('Failed to create product:', error);
      
      toast({
        variant: "destructive",
        title: "Creation Failed",
        description: error instanceof Error ? error.message : "Failed to create product. Please try again.",
      });
    }
  }, [pendingProductCreation, currentConversationId, updateConversationMessages]);

  const clearContext = useCallback(() => {
    setChatContext('');
  }, []);

  const clearMessages = useCallback(() => {
    if (!currentConversationId) return;
    updateConversationMessages(currentConversationId, () => []);
  }, [currentConversationId, updateConversationMessages]);

  const value: ChatContextType = {
    messages: currentConversation?.messages || [],
    conversations,
    archivedConversations,
    currentConversationId,
    isOpen,
    isMinimized,
    unreadCount,
    context: chatContext,
    hasStores,
    pendingChanges,
    pendingProductCreation,
    isLoading,
    isLoadingMessages,
    isLoadingArchived,
    regeneratingMessageId,
    sendMessage,
    retryMessage,
    regenerateResponse,
    openChat,
    closeChat,
    minimizeChat,
    toggleChat,
    setContext: setChatContext,
    clearContext,
    clearMessages,
    createNewConversation,
    switchConversation,
    deleteConversation,
    renameConversation,
    restoreConversation,
    permanentlyDeleteConversation,
    loadArchivedConversations,
    clearPendingChanges,
    approvePendingChanges,
    clearPendingProductCreation,
    approvePendingProductCreation,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export function useChat() {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
}
